import React, { useEffect, useMemo, useState } from "react";
import {
  ProgressBar,
  Table,
  Container,
  Row,
  Col,
  Spinner,
  Button,
  Form,
} from "react-bootstrap";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import "../Marketing_Clients/Shop_statistic/statistic.css";
import BackArrow from "../../../components/backArrow";
import MarketingRatingModel from "../Rating_Modal";
import MassengerModals from "../../../components/Navbar/MassengerModal";
import useShopStatistics from "../../../hooks/useShopStatistics";
import {
  useGetHiredMarketersByShopQuery,
  useGetMarketerByMarketerIdQuery,
} from "../../../features/marketing/marketingApiSlice";
import { getAsset } from "../../../helpers/helper";
import dummyUser from "../../../assets/images/dummy/user-avatar-img.jpg";
import AdvertiserAnalyticsCharts from "./AdvertiserAnalyticsCharts";

const RATIO_KEYS = {
  5: "fiveStar",
  4: "fourStar",
  3: "threeStar",
  2: "twoStar",
  1: "oneStar",
};

const renderStars = (rating = 0) => {
  const filled = Math.floor(Number(rating) || 0);
  return [...Array(5)].map((_, i) => (
    <span
      key={i}
      className={i < filled ? "perf-star-filled" : "perf-star-empty"}
    >
      ★
    </span>
  ));
};

const formatReviewDateTime = (dateInput) => {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} · ${timePart}`;
};

const resolveAvatar = (imageUrl) => {
  if (!imageUrl || imageUrl === "N/A") return dummyUser;
  const src = Array.isArray(imageUrl) ? imageUrl[0] : imageUrl;
  if (!src || typeof src !== "string" || !src.trim()) return dummyUser;
  return getAsset(src) || dummyUser;
};

/** Display status from Flask ad lifecycle (active / paused / expired / pending) */
const resolveAdDisplayStatus = (ad) => {
  const raw = String(ad?.status || "").toLowerCase();
  if (raw === "expired" || raw === "completed") {
    return { label: "Expired", className: "status-expired" };
  }
  if (raw === "paused") {
    return { label: "Paused", className: "status-expired" };
  }
  if (raw === "pending" || raw === "waiting" || raw === "submitted") {
    return { label: "Pending", className: "status-expired" };
  }
  if (raw === "rejected") {
    return { label: "Rejected", className: "status-expired" };
  }

  const end = ad?.endDate ? new Date(ad.endDate) : null;
  if (end && !Number.isNaN(end.getTime())) {
    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);
    if (endOfDay < new Date()) {
      return { label: "Expired", className: "status-expired" };
    }
  }

  if (raw === "active" || raw === "running" || raw === "scheduled") {
    return { label: "Active", className: "status-active" };
  }

  return {
    label: ad?.status ? String(ad.status) : "—",
    className: "status-expired",
  };
};

const formatAdDate = (value) => {
  if (!value) return "—";
  // Support YYYY-MM-DD (UTC noon avoids timezone day-shift)
  const raw = String(value).trim();
  const d =
    /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? new Date(`${raw}T12:00:00`)
      : new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ShopsideStatistics = () => {
  const [smShow, setSmShow] = useState(false);
  const [lgMsgShow, setLgMsgShow] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const {
    shopid,
    isLoading,
    isAdsLoading,
    isAdsError,
    isError,
    isSubError,
    error,
    adsError,
    refetch,
    refetchAds,
    ads,
    adMetrics,
    planName,
    postAdsLimit,
    adsUsedCount,
    adsRemaining,
    adsUsedPercentage,
    itemsLimit,
    postsCreated,
    postsRemaining,
    postsUsedPercentage,
    shareLimit,
    totalShares,
    sharesRemaining,
    sharesUsedPercentage,
    hasPurchaseData,
    purchase,
  } = useShopStatistics();

  const totalImpressions = adMetrics?.impressions ?? 0;
  const totalClicks = adMetrics?.clicks ?? 0;
  const totalConversion = adMetrics?.conversion ?? 0;
  const activeAdsCount = adMetrics?.activeCount ?? 0;
  const approvedAdsCount = adMetrics?.approvedCount ?? 0;
  const eligibleAdsCount = adMetrics?.eligibleCount ?? 0;
  const totalDailyBudget = adMetrics?.dailyBudget ?? 0;
  const totalDailySpent = adMetrics?.dailySpent ?? 0;
  const totalDailyRemaining = adMetrics?.dailyRemaining ?? 0;
  const campaignSpent = adMetrics?.spent ?? 0;
  const campaignRemaining = adMetrics?.remainingBudget ?? 0;
  const campaignTotalBudget = adMetrics?.totalBudget ?? 0;
  const computeEligible =
    adMetrics?.computeEligible ||
    ((ad) =>
      ad?.eligible === true || ad?.eligibility?.eligible === true);
  const resolveCampaignBudget =
    adMetrics?.resolveAdCampaignBudget ||
    ((ad) => {
      const spent = Number(ad?.spent) || 0;
      const total = Number(ad?.totalBudget) || 0;
      const remaining =
        total > 0
          ? Math.max(Math.round((total - spent) * 100) / 100, 0)
          : Math.max(Number(ad?.remaining) || 0, 0);
      return { spent, total, remaining };
    });
  const resolveDailyBudget =
    adMetrics?.resolveAdDailyBudget ||
    ((ad) => {
      const dailyBudget = Number(ad?.dailyBudget ?? ad?.budget) || 0;
      const dailySpent = Number(ad?.dailySpent) || 0;
      return {
        dailyBudget,
        dailySpent,
        dailyRemaining: Math.max(dailyBudget - dailySpent, 0),
      };
    });

  const shopIdForRating = shopid || purchase?.resolvedShopId || null;

  const {
    data: hiredRes,
    isLoading: isHiredLoading,
    isFetching: isHiredFetching,
  } = useGetHiredMarketersByShopQuery(shopIdForRating, {
    skip: !shopIdForRating,
    refetchOnMountOrArgChange: true,
  });

  const hiredMarketers = useMemo(() => {
    const list = hiredRes?.data;
    return Array.isArray(list) ? list : [];
  }, [hiredRes]);

  useEffect(() => {
    if (!hiredMarketers.length) {
      setSelectedAgentId(null);
      return;
    }
    setSelectedAgentId((prev) => {
      if (prev && hiredMarketers.some((m) => m.agentMongoId === prev)) {
        return prev;
      }
      return hiredMarketers[0].agentMongoId;
    });
  }, [hiredMarketers]);

  const selectedHired = useMemo(
    () =>
      hiredMarketers.find((m) => m.agentMongoId === selectedAgentId) || null,
    [hiredMarketers, selectedAgentId],
  );

  const {
    data: marketerRes,
    isFetching: isMarketerFetching,
  } = useGetMarketerByMarketerIdQuery(selectedAgentId, {
    skip: !selectedAgentId,
    refetchOnMountOrArgChange: true,
  });

  const agent = useMemo(() => {
    const raw = marketerRes?.data;
    if (!raw || (!raw._id && !raw.marketerId && !raw.userId)) return null;
    return raw;
  }, [marketerRes]);

  const topReview = marketerRes?.topReviewSection || null;
  const averageRating =
    Number(topReview?.averageRating) ||
    Number(selectedHired?.averageRating) ||
    0;
  const totalReviews =
    Number(topReview?.totalReviews) ||
    Number(selectedHired?.totalReviews) ||
    0;
  const ratingRatio = topReview?.ratingRatio || {};

  // Prefer hired-list myShopReview; also resolve from marketer ratings in case
  // the review was saved under public shopid vs Mongo _id (mismatch).
  const myShopReview = useMemo(() => {
    if (selectedHired?.myShopReview) return selectedHired.myShopReview;

    const shopKeys = new Set(
      [
        shopIdForRating,
        purchase?.resolvedShopId,
        purchase?.shopid?._id,
        purchase?.shopid?.shopid,
        purchase?.shopid,
        localStorage.getItem("OpenShopIdForMarketingByMarketer"),
        localStorage.getItem("shopid"),
      ]
        .filter(Boolean)
        .map(String),
    );

    const reviews = Array.isArray(marketerRes?.ratings)
      ? marketerRes.ratings
      : Array.isArray(agent?.ratingReviews)
        ? agent.ratingReviews
        : [];

    const found =
      reviews.find((r) =>
        shopKeys.has(
          String(r?.shopid?._id || r?.shopid?.shopid || r?.shopid || ""),
        ),
      ) ||
      // Legacy: hired marketer with exactly one review that didn't match ids
      (selectedHired && reviews.length === 1 ? reviews[0] : null);

    if (!found) return null;
    return {
      rating: found.rating,
      comment: found.comment || "",
      createdAt: found.createdAt,
    };
  }, [
    selectedHired,
    shopIdForRating,
    purchase,
    marketerRes,
    agent,
  ]);

  const hasMyReview = Boolean(myShopReview);
  const hasHiredAgent = Boolean(selectedHired);

  const agentName =
    selectedHired?.fullname ||
    agent?.userId?.fullname ||
    agent?.fullname ||
    "Not assigned";
  const agentPublicId =
    selectedHired?.marketerId || agent?.marketerId || selectedAgentId || "—";
  const commissionType =
    selectedHired?.commissionType ||
    agent?.commissionType ||
    agent?.paymentType ||
    "Percentage";
  const agentAvatar = resolveAvatar(
    selectedHired?.imageUrl ||
    agent?.userId?.imageUrl ||
    agent?.imageUrl ||
    null,
  );

  const chatUser = useMemo(() => {
    if (!selectedHired?.userId && !agent) return null;
    const user =
      agent?.userId && typeof agent.userId === "object"
        ? agent.userId
        : null;
    const img = Array.isArray(user?.imageUrl)
      ? user.imageUrl[0]
      : user?.imageUrl || selectedHired?.imageUrl || null;
    return {
      _id: selectedHired?.userId || user?._id || agent?.userId,
      fullname: agentName,
      image: img,
    };
  }, [selectedHired, agent, agentName]);

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center py-5 min-vh-100">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading statistics...</span>
        </Spinner>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-5 text-center">
        <div className="alert alert-danger" role="alert">
          {error?.data?.message ||
            error?.message ||
            "No shop selected. Open statistics from your shop profile."}
        </div>
      </Container>
    );
  }

  return (
    <Container style={{ background: "#f8fbff" }}>
      <MarketingRatingModel
        smShow={smShow}
        setSmShow={setSmShow}
        shopid={shopIdForRating}
        marketerId={selectedHired?.agentMongoId}
        publicMarketerId={selectedHired?.marketerId}
        agentMongoId={selectedHired?.agentMongoId}
        agentName={hasHiredAgent ? agentName : ""}
      />
      <MassengerModals
        lgMsgShow={lgMsgShow}
        setLgMsgShow={setLgMsgShow}
        initialUser={chatUser}
        onInitialUserConsumed={() => { }}
      />

      <Row className="p-5 pt-0">
        <div className="screenHeight">
          <BackArrow />
          <h4 className="mt-2">Shop Statistics</h4>

          <div className="package-info">
            <span>
              Package active: <strong>{planName}</strong>
            </span>
          </div>

          {isSubError && !hasPurchaseData && (
            <div
              className="alert alert-warning mt-2 d-flex align-items-center justify-content-between"
              role="alert"
            >
              <span>
                Could not load statistics. Check that the API server is running,
                then retry.
              </span>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => refetch?.()}
              >
                Retry
              </Button>
            </div>
          )}

          <div className="section mt-3">
            <h3 className="section-title">Package Progress Tracker</h3>

            <div className="progress-item">
              <span className="progress-label">Posts created</span>
              <span className="progress-value">
                {`${postsCreated ?? 0}/${itemsLimit ?? 0}`}
              </span>
              <ProgressBar
                now={postsUsedPercentage || 0}
                className="custom-progress"
              />
              <small className="remaining">{postsRemaining ?? 0} Remaining</small>
            </div>

            <div className="progress-item">
              <span className="progress-label">Ads Used</span>
              <span className="progress-value">
                {isAdsLoading
                  ? "…"
                  : `${adsUsedCount ?? 0}/${postAdsLimit ?? 0}`}
              </span>
              <ProgressBar
                now={adsUsedPercentage || 0}
                className="custom-progress"
              />
              <small className="remaining">{adsRemaining ?? 0} Remaining</small>
            </div>

            <div className="progress-item">
              <span className="progress-label">Shared to no of users</span>
              <span className="progress-value">
                {`${totalShares ?? 0}/${shareLimit ?? 0}`}
              </span>
              <ProgressBar
                now={sharesUsedPercentage || 0}
                className="custom-progress"
              />
              <small className="remaining">
                {sharesRemaining ?? 0} Remaining
              </small>
            </div>

            <div className="progress-item">
              <div className="progress-label">
                Goal ({adsUsedPercentage}% Reached)
              </div>
              <div className="progress-value">
                {adsUsedPercentage === 100
                  ? "Goal Achieved"
                  : activeAdsCount > 0
                    ? `${activeAdsCount} Active Ads Running`
                    : approvedAdsCount > 0
                      ? `${approvedAdsCount} Ads Approved`
                      : ads.length
                        ? "Ads Created"
                        : "No Ads"}
              </div>
              <ProgressBar
                now={ads.length ? Math.max(adsUsedPercentage, 10) : 10}
                className="custom-progress"
              />
            </div>

            <div className="progress-item">
              <span className="progress-label">Total Impressions</span>
              <span className="progress-value">{totalImpressions}</span>
              <ProgressBar
                now={totalImpressions > 0 ? Math.min(100, totalImpressions) : 0}
                className="custom-progress"
              />
              <small className="remaining">
                {totalClicks} clicks · {totalConversion}% conversion
              </small>
            </div>

            <div className="progress-item">
              <span className="progress-label">Eligible (live) ads</span>
              <span className="progress-value">
                {`${eligibleAdsCount}/${ads.length || 0}`}
              </span>
              <ProgressBar
                now={
                  ads.length
                    ? Math.round((eligibleAdsCount / ads.length) * 100)
                    : 0
                }
                className="custom-progress"
              />
              <small className="remaining">
                Approved · Paid · Active · In date · Budget left
              </small>
            </div>

            <div className="progress-item">
              <span className="progress-label">Today&apos;s ad budget</span>
              <span className="progress-value">
                {`$${Number(totalDailySpent).toFixed(2)} / $${Number(totalDailyBudget).toFixed(2)}`}
              </span>
              <ProgressBar
                now={
                  totalDailyBudget > 0
                    ? Math.min(
                      100,
                      Math.round((totalDailySpent / totalDailyBudget) * 100),
                    )
                    : 0
                }
                className="custom-progress"
              />
              <small className="remaining">
                ${Number(totalDailyRemaining).toFixed(2)} remaining today
              </small>
            </div>
          </div>

          {/* Advertiser Analytics Dashboard (Flask metrics) */}
          <div className="section">
            <h3 className="section-title">Advertiser Analytics</h3>
            <Row className="g-3 mb-2">
              <Col xs={6} md={3}>
                <div className="progress-item mb-0">
                  <span className="progress-label">Impressions</span>
                  <span className="progress-value">
                    {totalImpressions.toLocaleString()}
                  </span>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="progress-item mb-0">
                  <span className="progress-label">Clicks</span>
                  <span className="progress-value">
                    {totalClicks.toLocaleString()}
                  </span>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="progress-item mb-0">
                  <span className="progress-label">CTR</span>
                  <span className="progress-value">{totalConversion}%</span>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="progress-item mb-0">
                  <span className="progress-label">Spent</span>
                  <span className="progress-value">
                    ${Number(campaignSpent).toFixed(2)}
                  </span>
                  <small className="remaining d-block">
                    ${Number(campaignRemaining).toFixed(2)} remaining
                    {campaignTotalBudget > 0
                      ? ` of $${Number(campaignTotalBudget).toFixed(2)}`
                      : ""}
                  </small>
                </div>
              </Col>
            </Row>
            <AdvertiserAnalyticsCharts ads={ads} adMetrics={adMetrics} />
          </div>

          <div className="section">
            <h3 className="section-title">Ad Performance Table</h3>

            {isAdsError ? (
              <div
                className="alert alert-warning d-flex align-items-center justify-content-between"
                role="alert"
              >
                <span>
                  {(() => {
                    const raw =
                      adsError?.data?.message ||
                      adsError?.error ||
                      "";
                    if (
                      !raw ||
                      /failed to fetch|typeerror|networkerror|load failed/i.test(
                        String(raw),
                      )
                    ) {
                      return "Could not load ads. Start the Flask ads API (port 5393), then retry.";
                    }
                    return raw;
                  })()}
                </span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => refetchAds?.()}
                >
                  Retry
                </Button>
              </div>
            ) : null}

            <div className="table-container">
              <Table borderless className="performance-table">
                <thead>
                  <tr>
                    <th>Ad Title</th>
                    <th>Targeting</th>
                    <th>Impressions</th>
                    <th>Clicks</th>
                    <th>Conversion %</th>
                    <th>Today Budget</th>
                    <th>Date Posted</th>
                    <th>Status</th>
                    <th>Eligible</th>
                    <th>CTR</th>
                  </tr>
                </thead>

                <tbody>
                  {ads.length > 0 ? (
                    ads.map((ad, i) => {
                      const impressions = Number(ad.impressions) || 0;
                      const clicks = Number(ad.clicks) || 0;
                      const conversion =
                        impressions > 0
                          ? ((clicks / impressions) * 100).toFixed(2)
                          : "0.00";
                      const displayStatus = resolveAdDisplayStatus(ad);
                      const dailyCap =
                        Number(
                          ad.dailyImpressionCap ??
                          ad.eligibility?.dailyImpressionCap,
                        ) || 0;
                      const dailyUsed =
                        Number(
                          ad.dailyImpressions ??
                          ad.eligibility?.dailyImpressions,
                        ) || 0;
                      const {
                        dailyBudget,
                        dailySpent,
                        dailyRemaining,
                      } = resolveDailyBudget(ad);
                      const targeting =
                        ad.targeting || ad.eligibility?.targeting || {};
                      const ageLabel =
                        targeting.ageMin != null || targeting.ageMax != null
                          ? `${targeting.ageMin ?? "—"}-${targeting.ageMax ?? "—"}`
                          : "—";
                      const isEligible = Boolean(computeEligible(ad));
                      const currency = ad.currency || "USD";
                      const placementLabel = (
                        ad.placement ||
                        (ad.placements || [])[0] ||
                        ad.eligibility?.placements?.[0] ||
                        "—"
                      );
                      const {
                        spent,
                        remaining,
                        total: adTotalBudget,
                      } = resolveCampaignBudget(ad);
                      const remainingLabel = remaining.toFixed(2);

                      return (
                        <tr key={ad._id || ad.ad_id || i}>
                          <td className="ad-title">
                            {ad.title || ad.description || "Untitled ad"}
                            <small className="d-block text-muted">
                              Placement: {placementLabel}
                              {dailyCap > 0
                                ? ` · Today ${dailyUsed}/${dailyCap} imp`
                                : ""}
                            </small>
                          </td>
                          <td>
                            <small className="d-block">
                              {[targeting.country, targeting.state, targeting.city]
                                .filter(Boolean)
                                .join(" / ") || "—"}
                            </small>
                            <small className="text-muted">
                              {ageLabel} · {targeting.gender || "all"} ·{" "}
                              {targeting.language || "—"}
                            </small>
                          </td>
                          <td>{impressions}</td>
                          <td>{clicks}</td>
                          <td>{conversion}%</td>
                          <td>
                            <small className="d-block">
                              {currency} {dailySpent.toFixed(2)} /{" "}
                              {dailyBudget.toFixed(2)}
                            </small>
                            <small className="text-muted">
                              {dailyRemaining.toFixed(2)} left today
                            </small>
                            <small className="d-block text-muted">
                              Spent {spent.toFixed(2)} · Rem {remainingLabel}
                              {adTotalBudget > 0
                                ? ` / ${adTotalBudget.toFixed(2)}`
                                : ""}
                            </small>
                          </td>
                          <td>{formatAdDate(ad.createdAt || ad.startDate)}</td>
                          <td>
                            <span className={displayStatus.className}>
                              {displayStatus.label}
                            </span>
                          </td>
                          <td>
                            <span
                              className={
                                isEligible
                                  ? "status-active"
                                  : "status-expired"
                              }
                            >
                              {isEligible ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>{conversion}%</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" style={{ textAlign: "center" }}>
                        {isAdsLoading
                          ? "Loading ads..."
                          : "No Ads Found for this shop"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {/* ── Original Performance Metrics UI ── */}
          <div className="perf-metrics-section">
            <h3 className="perf-metrics-title">Performance Metrics</h3>

            {hiredMarketers.length > 1 && (
              <div className="hired-marketer-tabs">
                {hiredMarketers.map((m) => (
                  <button
                    key={m.agentMongoId}
                    type="button"
                    className={`hired-marketer-tab${m.agentMongoId === selectedAgentId ? " active" : ""
                      }`}
                    onClick={() => setSelectedAgentId(m.agentMongoId)}
                  >
                    {m.fullname}
                  </button>
                ))}
              </div>
            )}

            {(isHiredLoading || isHiredFetching || isMarketerFetching) &&
              !hasHiredAgent ? (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" />
                Loading marketer profile...
              </div>
            ) : !hasHiredAgent ? (
              <p className="text-muted mb-0">
                No marketer hired yet. Hire an approved marketer to see their
                performance here.
              </p>
            ) : (
              <div className="perf-agent-row">
                <img
                  src={agentAvatar}
                  alt={agentName}
                  className="perf-agent-avatar"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = dummyUser;
                  }}
                />
                <div className="perf-agent-meta">
                  <p className="perf-agent-name">Agent: {agentName}</p>
                  <p className="perf-agent-details">
                    Agent ID: {agentPublicId} | Rating:{" "}
                    {averageRating ? averageRating.toFixed(1) : "0.0"}/5 |
                    Commission Type: {commissionType}
                  </p>
                  {/* After review: Message only (no Rate button) */}
                  <Button
                    className="perf-message-btn"
                    disabled={!chatUser?._id}
                    onClick={() => setLgMsgShow(true)}
                  >
                    Message
                    <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ── Original Give Feedback & Rating UI ── */}
          <div className="perf-feedback-section">
            <h3 className="perf-feedback-title">Give Feedback &amp; Rating</h3>

            <Row className="align-items-center g-4 mb-4">
              <Col xs={12} md={4} className="text-md-start text-center">
                <div className="perf-score-value">
                  {averageRating ? averageRating.toFixed(1) : "0.0"}
                </div>
                <div className="perf-stars">{renderStars(averageRating)}</div>
                <div className="perf-reviews-count">
                  {totalReviews} reviews
                </div>
              </Col>

              <Col xs={12} md={8}>
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = Number(ratingRatio?.[RATIO_KEYS[star]]) || 0;
                  const percent =
                    totalReviews > 0
                      ? Math.round((count / totalReviews) * 100)
                      : 0;
                  return (
                    <div key={star} className="perf-rating-row">
                      <span className="perf-rating-label">{star}</span>
                      <ProgressBar now={percent} className="perf-rating-bar" />
                      <span className="perf-rating-pct">{percent}%</span>
                    </div>
                  );
                })}
              </Col>
            </Row>

            {hasMyReview ? (
              <div className="hired-my-review">
                <p className="hired-my-review-label">Your review</p>
                <div className="perf-stars">
                  {renderStars(myShopReview.rating)}
                </div>
                {myShopReview.comment ? (
                  <p className="hired-my-review-comment">
                    {myShopReview.comment}
                  </p>
                ) : null}
                <p className="text-muted mb-0 mt-1" style={{ fontSize: 12 }}>
                  {formatReviewDateTime(myShopReview.createdAt)}
                </p>
              </div>
            ) : (
              <Form.Control
                as="textarea"
                rows={5}
                placeholder={
                  hasHiredAgent
                    ? "Write your feedback here..."
                    : "Hire an approved marketer to leave feedback"
                }
                className="perf-feedback-input"
                readOnly
                disabled={!hasHiredAgent}
                onClick={() => {
                  if (hasHiredAgent && shopIdForRating) setSmShow(true);
                }}
              />
            )}
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default ShopsideStatistics;
